/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { PhyReconAdjustmentReason } from "../StartReconciliation.types";
import { useEffect, useState } from "react";
import { mediaReconciliationService } from "../../../../../service/mediaReconciliationServices";

interface ReconcileMediaTableProps {
	data: any[];
	mediaType: string;
	unit: string;
	label: string;
	batchName: string;
	handleUnitChange: (lotId: string, value: string) => void;
	handleReasonChange: (lotId: string, value: string) => void;
	reasonErrors: Record<string, string>;
	emptyMessage?: string;
}

const SReconcileMediaTable: React.FC<ReconcileMediaTableProps> = ({
	data,
	unit,
	label,
	batchName,
	mediaType,
	handleUnitChange,
	handleReasonChange,
	reasonErrors,
	emptyMessage = "No data available",
}) => {
	const { t } = useTranslation();

	const [adjustmentReasons, setAdjustmentReasons] = useState<PhyReconAdjustmentReason[]>([]);
	const [unitChangeStep, setUnitChangeStep] = useState<string>("0.01");
	const fetchReasons = async (category: string) => {
		const response = await mediaReconciliationService.getReconMediaAdjustmentReasons(category);
		setAdjustmentReasons(response.data);
	}

	useEffect(() => {
		switch (mediaType) {
			case "container":
				fetchReasons("CONTAINER");
				setUnitChangeStep("1");
				break;
			default:
				fetchReasons("GENERAL");
				setUnitChangeStep("0.01");
				break;
		}
	}, [mediaType]);

	return (
		<>
			<div className="bg-[#2D3E50] px-6 py-4 flex items-center gap-3">
				<span className="material-symbols-outlined text-white text-xl">
					analytics
				</span>
				<h3 className="text-sm font-black text-white tracking-tight">
					{t("mediaReconcile.physicalReconciliation.containerBreakdown")}:
					Lot #{batchName}
				</h3>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-left">
					{/* TABLE HEADER */}
					<thead className="bg-[#2D3E50] text-white sticky top-0 z-20">
						<tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
							<th className="px-6 py-4">
								{t(
									"mediaReconcile.physicalReconciliation.columns.lotNumber",
								)}
							</th>
							<th className="px-6 py-4">
								{t(
									"mediaReconcile.physicalReconciliation.columns.system",
								)}{" "}
								{label} ({unit})
							</th>
							<th className="px-6 py-4 text-center">
								{t(
									"mediaReconcile.physicalReconciliation.columns.physical",
								)}{" "}
								{label} ({unit})
							</th>
							<th className="px-6 py-4 text-center">
								{t(
									"mediaReconcile.physicalReconciliation.columns.variance",
								)}{" "}
								({unit})
							</th>
							<th className="px-6 py-4">
								{t(
									"mediaReconcile.physicalReconciliation.columns.adjustmentReason",
								)}{" "}
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{data.map((item, idx) => {
							const variance =
								item.physical_quantity - item.system_quantity;
							return (
								<tr
									key={idx}
									className="hover:bg-slate-50 transition-colors group"
								>
									<td className="px-6 py-4 align-top font-bold text-slate-700 text-xs tracking-tight">
										{item.lot_code}
									</td>
									<td className="px-6 py-4 align-top font-bold text-slate-800 text-xs">
										{item.system_quantity.toLocaleString(undefined, {
											minimumFractionDigits: 2,
										})}
									</td>
									<td className="px-6 py-4 align-top">
										<div className="flex justify-center">
											<input
												type="number"
												step={unitChangeStep}
												value={item.physical_quantity}
												onChange={(e) =>
													handleUnitChange(
														item.lot_id,
														e.target.value,
													)
												}
												className="w-28 text-center bg-white border border-gray-200 rounded-md py-1.5 text-xs font-black text-slate-800 focus:ring-[#FF7043] focus:border-[#FF7043]"
											/>
										</div>
									</td>
									<td className="px-6 py-4 align-top text-center">
										<span
											className={`text-xs font-black ${variance < 0 ? "text-red-500" : variance > 0 ? "text-emerald-500" : "text-slate-300"}`}
										>
											{variance > 0 ? "+" : ""}
											{variance.toLocaleString(undefined, {
												minimumFractionDigits: 2,
											})}
										</span>
									</td>
									<td className="px-6 py-4 align-top">
										<select
											value={item.reason}
											onChange={(e) =>
												handleReasonChange(
													item.lot_id,
													e.target.value,
												)
											}
											disabled={variance === 0}
											className={`w-full py-1.5 text-[11px] font-bold border rounded-md focus:ring-[#FF7043] transition-all ${
												variance !== 0
													? "bg-white border-gray-200 text-slate-700"
													: "bg-gray-50 border-gray-100 text-slate-300 cursor-not-allowed"
											}`}
										>
											<option value="">Select Reason...</option>
											{adjustmentReasons.map((r) => (
												<option key={r.id} value={r.id}>
													{r.reason_name}
												</option>
											))}
										</select>
										{reasonErrors[item.lot_id] && (<p className="mt-1 text-xs text-red-500">{reasonErrors[item.lot_id]}</p>)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</>
	);
};

export default SReconcileMediaTable;
